import { useState, useCallback, useEffect } from 'react';
import type { DateRange } from 'react-day-picker';
import {
    fetchAdminDriverStats,
    fetchAdminDriverDetail,
    type DriverStatsSummary,
    type DriverDeliveryDetail,
} from '../services/driverStatsService';
import { getErrorMessage } from '../utils/errors';

export function useDriverStats() {
    const [dateRange, setDateRange] = useState<DateRange | undefined>({
        from: new Date(new Date(new Date().setDate(new Date().getDate() - 29)).setHours(0, 0, 0, 0)),
        to:   new Date(),
    });
    const [drivers,         setDrivers]         = useState<DriverStatsSummary[]>([]);
    const [loading,         setLoading]         = useState(true);
    const [error,           setError]           = useState<string | null>(null);
    const [selectedDriver,  setSelectedDriver]  = useState<DriverStatsSummary | null>(null);
    const [detailDeliveries, setDetailDeliveries] = useState<DriverDeliveryDetail[]>([]);
    const [detailLoading,   setDetailLoading]   = useState(false);

    const startDate = dateRange?.from
        ? new Date(Date.UTC(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate(), 0, 0, 0, 0)).toISOString()
        : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();

    const endDate = dateRange?.to
        ? new Date(Date.UTC(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59, 999)).toISOString()
        : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();

    const loadOverview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setDrivers(await fetchAdminDriverStats(startDate, endDate));
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load driver stats'));
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => { void loadOverview(); }, [loadOverview]);

    const openDetail = useCallback(async (driver: DriverStatsSummary) => {
        setSelectedDriver(driver);
        setDetailLoading(true);
        try {
            setDetailDeliveries(await fetchAdminDriverDetail(driver.driver_id, startDate, endDate));
        } catch {
            setDetailDeliveries([]);
        } finally {
            setDetailLoading(false);
        }
    }, [startDate, endDate]);

    return {
        dateRange, setDateRange,
        drivers, loading, error,
        selectedDriver, setSelectedDriver,
        detailDeliveries, detailLoading,
        openDetail,
    };
}